/**
 * 會員中心 - 儀表板
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  User, ShoppingBag, Lock, LogOut, 
  Package, Clock, CheckCircle, XCircle,
  Mail, Phone, MapPin
} from 'lucide-react';

export function MemberDashboard() {
  const navigate = useNavigate();
  const { user, signOut, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      fetchOrders();
    }
  }, [user]);

  const fetchOrders = async () => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .eq('recipient_email', user.email)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      setOrders(data || []);
    } catch (error) {
      console.error('查詢訂單失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'pending':
        return <Clock className="h-5 w-5 text-yellow-600" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-600" />;
      default:
        return <Package className="h-5 w-5 text-gray-600" />;
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      pending: '待付款',
      paid: '已付款',
      failed: '付款失敗',
      confirmed: '已確認',
      processing: '處理中',
      shipped: '已出貨',
      delivered: '已送達',
      cancelled: '已取消',
    };
    return statusMap[status] || status;
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-teal-50 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* 頁面標題 */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">會員中心</h1>
          <p className="text-gray-600">管理您的個人資料和訂單</p>
        </div>

        {/* 主要內容 */}
        <div className="grid lg:grid-cols-4 gap-6">
          {/* 左側：側邊欄 */}
          <div className="lg:col-span-1">
            <Card className="bg-white/80 backdrop-blur-xl border-white/20 shadow-lg sticky top-4">
              <CardHeader>
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white text-2xl font-bold mb-4">
                  {user.user_metadata?.name?.[0] || user.email[0].toUpperCase()}
                </div>
                <CardTitle className="text-lg">
                  {user.user_metadata?.name || '會員'}
                </CardTitle>
                <CardDescription className="text-sm break-all">
                  {user.email}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button
                  variant="ghost"
                  className="w-full justify-start"
                  onClick={() => navigate('/member')}
                >
                  <User className="h-4 w-4 mr-2" />
                  個人資料
                </Button>
                <Button
                  variant="ghost"
                  className="w-full justify-start"
                  onClick={() => setActiveTab('orders')}
                >
                  <ShoppingBag className="h-4 w-4 mr-2" />
                  訂單記錄
                </Button>
                <Button
                  variant="ghost"
                  className="w-full justify-start"
                  onClick={() => navigate('/member/change-password')}
                >
                  <Lock className="h-4 w-4 mr-2" />
                  變更密碼
                </Button>
                <div className="pt-4 border-t">
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={handleLogout}
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    登出
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* 右側：主要內容 */}
          <div className="lg:col-span-3">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
              <TabsList className="bg-white/80 backdrop-blur-xl border border-white/20">
                <TabsTrigger value="profile">個人資料</TabsTrigger>
                <TabsTrigger value="orders">訂單記錄</TabsTrigger>
              </TabsList>

              {/* 個人資料 */}
              <TabsContent value="profile" className="space-y-6">
                <Card className="bg-white/80 backdrop-blur-xl border-white/20 shadow-lg">
                  <CardHeader>
                    <CardTitle>個人資料</CardTitle>
                    <CardDescription>查看和更新您的個人資訊</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-start gap-3">
                      <User className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">姓名</p>
                        <p className="font-medium">{user.user_metadata?.name || '未設定'}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Mail className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">Email</p>
                        <p className="font-medium break-all">{user.email}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Phone className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-500">手機號碼</p>
                        <p className="font-medium">{user.user_metadata?.phone || '未設定'}</p>
                      </div>
                    </div>
                    <div className="pt-4">
                      <Button 
                        onClick={() => navigate('/member/edit-profile')}
                        className="bg-gradient-to-r from-blue-600 to-cyan-600"
                      >
                        編輯個人資料
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white/80 backdrop-blur-xl border-white/20 shadow-lg">
                  <CardHeader>
                    <CardTitle>帳號安全</CardTitle>
                    <CardDescription>管理您的密碼和安全設定</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button 
                      variant="outline"
                      onClick={() => navigate('/member/change-password')}
                    >
                      <Lock className="h-4 w-4 mr-2" />
                      變更密碼
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* 訂單記錄 */}
              <TabsContent value="orders" className="space-y-6">
                <Card className="bg-white/80 backdrop-blur-xl border-white/20 shadow-lg">
                  <CardHeader>
                    <CardTitle>訂單記錄</CardTitle>
                    <CardDescription>
                      查看您的歷史訂單和訂單狀態
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {loading ? (
                      <div className="flex justify-center py-8">
                        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    ) : orders.length === 0 ? (
                      <div className="text-center py-12">
                        <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                        <p className="text-gray-500 mb-4">尚無訂單記錄</p>
                        <Button onClick={() => navigate('/product')}>
                          前往購物
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {orders.map((order) => (
                          <div
                            key={order.id}
                            className="border rounded-lg p-4 hover:bg-gray-50/50 transition-colors cursor-pointer"
                            onClick={() => navigate(`/order-detail/${order.order_number}`)}
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div>
                                <p className="font-mono text-sm text-gray-500 mb-1">
                                  {order.order_number}
                                </p>
                                <p className="font-medium">{order.product_name}</p>
                                <p className="text-sm text-gray-500">
                                  數量: {order.quantity} 件
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-lg font-bold text-blue-600">
                                  NT$ {order.total_amount.toLocaleString()}
                                </p>
                                <div className="flex items-center gap-2 mt-2">
                                  {getStatusIcon(order.payment_status)}
                                  <span className="text-sm">
                                    {getStatusText(order.payment_status)}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center justify-between text-xs text-gray-500">
                              <span>下單時間: {new Date(order.created_at).toLocaleString('zh-TW')}</span>
                              <span className="text-blue-600">查看詳情 →</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MemberDashboard;
